import { z } from 'zod';
import { vmsg } from '@/lib/validation';

const currentYear = new Date().getFullYear();

const optionalString = z
  .string()
  .trim()
  .optional()
  .transform((v) => (v === '' ? undefined : v));

export const candidateProfileSchema = z.object({
  fullName: z.string().trim().min(2, vmsg('required')),
  phone: z
    .string()
    .trim()
    .regex(/^[6-9]\d{9}$/, vmsg('phoneInvalid')),
  email: z
    .string()
    .trim()
    .email(vmsg('emailInvalid'))
    .optional()
    .or(z.literal('')),
  description: z
    .string()
    .trim()
    .refine((v) => v.split(/\s+/).filter(Boolean).length <= 100, vmsg('maxWords', { count: 100 }))
    .optional()
    .or(z.literal('')),
  gender: z
    .enum(['male', 'female', 'other', 'prefer_not_to_say'])
    .optional()
    .or(z.literal('').transform(() => undefined)),
  highestEducation: optionalString,
  itiTrade: optionalString,
  itiCollege: optionalString,
  department: optionalString,
  graduationYear: z.coerce
    .number()
    .int()
    .min(1980, vmsg('yearInvalid'))
    .max(currentYear, vmsg('yearInvalid'))
    .optional()
    .or(z.literal('').transform(() => undefined)),
  workExperienceMonths: z.coerce
    .number()
    .int()
    .min(0, vmsg('numberInvalid'))
    .max(600)
    .optional()
    .or(z.literal('').transform(() => undefined)),
  skills: z.array(z.string().trim().min(1)).optional(),
  workExperiences: z
    .array(
      z.object({
        company: z.string().trim().min(1, vmsg('required')),
        role: z.string().trim().min(1, vmsg('required')),
        fromMonth: optionalString,
        toMonth: optionalString,
        current: z.boolean().optional(),
        description: optionalString,
      }),
    )
    .optional(),
  educations: z
    .array(
      z.object({
        degree: z.string().trim().min(1, vmsg('required')),
        institution: z.string().trim().min(1, vmsg('required')),
        field: optionalString,
        year: z.coerce
          .number()
          .int()
          .min(1980, vmsg('yearInvalid'))
          .max(currentYear, vmsg('yearInvalid'))
          .optional()
          .or(z.literal('').transform(() => undefined)),
      }),
    )
    .optional(),
  city: optionalString,
  district: optionalString,
  pincode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, vmsg('pincodeInvalid'))
    .optional()
    .or(z.literal('')),
});

export type CandidateProfileForm = z.input<typeof candidateProfileSchema>;
